import { onMount, Show } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { Button } from "@lightningtv/solid-ui";
import styles from "../../styles";

const LAYOUT = [
    ["A", "B", "C", "D", "E", "F"],
    ["G", "H", "I", "J", "K", "L"],
    ["M", "N", "O", "P", "Q", "R"],
    ["S", "T", "U", "V", "W", "X"],
    ["Y", "Z", "1", "2", "3", "4"],
    ["5", "6", "7", "8", "9", "0"]
];

// Sized/spaced locally rather than via the shared styles.buttonKey /
// styles.searchSpace defaults, so the other search pages that use those
// shared styles are unaffected.
const KEY_SIZE = 58;
const KEY_PITCH = 62; // spacing between keys, key + small gap
// Sized so space+gap+backspace lines up with the character grid's right
// edge below it (5*KEY_PITCH + KEY_SIZE = 368)
const SPACE_W = 180;
const SPACE_H = 58;

type SearchKeyboardProps = {
    inputValue: () => string;
    isInitializing: () => boolean;
    hasError: () => boolean;
    onAddCharacter: (char: string) => void;
    onAddSpace: () => void;
    onRemoveCharacter: () => void;
    // Escalates to the parent when Up is pressed on the top row (space/
    // backspace) — e.g. to send focus to Home's header.
    onUp?: () => void;
    // The results grid isn't part of this component, but Right on the last
    // character column should land there — a getter, since that ref isn't
    // set until the results render.
    resultsGridRef?: () => any;
    // Same imperative-handle pattern as PinKeyboard's keyboardRef — a plain
    // object this component attaches methods to, so the parent can focus
    // the first key (e.g. once results finish loading) and read back
    // whichever key was last focused (e.g. to return to it from the
    // results grid).
    keyboardRef?: { focusFirstKey?: () => void; getLastFocusedKey?: () => any };
};

// The search screen's full alphanumeric keyboard: the typed-text display,
// space/backspace, and the character grid. Behavior/appearance intentionally
// unchanged from SmartSearchPanel — this is that same block, just extracted.
const SearchKeyboard = (props: SearchKeyboardProps) => {
    const refs: Array<Array<any>> = LAYOUT.map(() => []);
    let spaceRef: any, backspaceRef: any;
    let lastFocusedKey: any;

    const handleKeypadInteraction = (callback: () => void) => {
        if (!props.isInitializing() && !props.hasError()) {
            callback();
        }
    };

    onMount(() => {
        if (props.keyboardRef) {
            props.keyboardRef.focusFirstKey = () => refs[0]?.[0]?.setFocus?.();
            props.keyboardRef.getLastFocusedKey = () => lastFocusedKey;
        }
    });

    return (
        <>
            <View
                x={100}
                y={150}
                style={{
                    width: 1720,
                    height: 80,
                    borderRadius: 10,
                    color: hexColor("#00000000"),
                    border: { width: 2, color: hexColor("#FFFFFF33") },
                }}
            >
                <View x={22} y={20} width={40} height={40} src="https://i.ibb.co/nNhBcw8M/search.png" style={{ color: hexColor("#8E8E93") }} />
                <Show
                    when={props.inputValue()}
                    fallback={
                        <Text x={85} y={28} fontSize={28} color={hexColor("#8E8E93")}>
                            Type to search
                        </Text>
                    }
                >
                    <Text x={76} y={28} fontSize={34} color={hexColor("#ffffff")}>
                        {props.inputValue()}
                    </Text>
                </Show>
            </View>

            {/* Space and Backspace Keys */}
            <View
                ref={(el) => (spaceRef = el)}
                x={100}
                y={350}
                forwardStates style={{ ...styles.searchSpace, width: SPACE_W, height: SPACE_H }}
                onEnter={() => handleKeypadInteraction(() => props.onAddSpace())}
                onDown={() => handleKeypadInteraction(() => refs[0][0]?.setFocus())}
                onRight={() => handleKeypadInteraction(() => backspaceRef.setFocus())}
                onUp={() => handleKeypadInteraction(() => props.onUp?.())}
            >
                <View src={"https://i.ibb.co/G4SDmcq9/space.png"} width={72} height={58} x={54} y={0} style={{ color: "#ffffff", focus: { color: "#000000" } }} />
            </View>

            <View
                ref={(el) => (backspaceRef = el)}
                x={110 + SPACE_W}
                y={350}
                forwardStates style={{ ...styles.searchSpace, width: SPACE_W, height: SPACE_H }}
                onEnter={() => handleKeypadInteraction(() => props.onRemoveCharacter())}
                onDown={() => handleKeypadInteraction(() => refs[0][0]?.setFocus())}
                onLeft={() => handleKeypadInteraction(() => spaceRef.setFocus())}
                onUp={() => handleKeypadInteraction(() => props.onUp?.())}
            >
                <View src={"https://i.ibb.co/wh6n5N3X/backspace.png"} width={50} height={44} y={7} x={72} style={{ color: "#ffffff", focus: { color: "#000000" } }} />
            </View>

            {/* Character Keys */}
            <View x={100} y={420}>
                {LAYOUT.map((row, rowIndex) => (
                    <View y={rowIndex * KEY_PITCH}>
                        {row.map((char, colIndex) => (
                            <Button
                                ref={(el) => {
                                    refs[rowIndex][colIndex] = el;
                                    el.onFocus = () => {
                                        if (!props.isInitializing() && !props.hasError()) {
                                            lastFocusedKey = el;
                                        }
                                    };
                                }}
                                style={{ ...styles.buttonKey, width: KEY_SIZE, height: KEY_SIZE }}
                                x={colIndex * KEY_PITCH}
                                onEnter={() => handleKeypadInteraction(() => props.onAddCharacter(char))}
                                onRight={() => {
                                    handleKeypadInteraction(() => {
                                        const next = refs[rowIndex][colIndex + 1];
                                        if (next) {
                                            next.setFocus();
                                        } else {
                                            props.resultsGridRef?.()?.setFocus();
                                        }
                                    });
                                }}
                                onLeft={() => {
                                    handleKeypadInteraction(() => {
                                        const prev = refs[rowIndex][colIndex - 1];
                                        if (prev) prev.setFocus();
                                    });
                                }}
                                onDown={() => {
                                    handleKeypadInteraction(() => {
                                        const down = refs[rowIndex + 1]?.[colIndex];
                                        if (down) down.setFocus();
                                    });
                                }}
                                onUp={() => {
                                    handleKeypadInteraction(() => {
                                        if (rowIndex === 0) {
                                            spaceRef?.setFocus();
                                        } else {
                                            const up = refs[rowIndex - 1]?.[colIndex];
                                            if (up) up.setFocus();
                                        }
                                    });
                                }}
                            >
                                <Text fontSize={28} x={18} y={16}>{char}</Text>
                            </Button>
                        ))}
                    </View>
                ))}
            </View>
        </>
    );
};

export default SearchKeyboard;
